/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppData } from '../../context/AppDataContext';
import { settingsAPI, authAPI, orgDeletionAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DataExport from './DataExport';
import PasswordRules from '../common/PasswordRules';
import { validatePassword } from '../../utils/passwordPolicy';
import { BillingSettings } from '../billing/BillingSettings';

const MONTHS = [
  {value:'01',label:'January'},{value:'02',label:'February'},{value:'03',label:'March'},
  {value:'04',label:'April'},{value:'05',label:'May'},{value:'06',label:'June'},
  {value:'07',label:'July'},{value:'08',label:'August'},{value:'09',label:'September'},
  {value:'10',label:'October'},{value:'11',label:'November'},{value:'12',label:'December'}
];

// Days available per month (use 28 as safe max for all months)
const DAYS = Array.from({length:28},(_,i)=>String(i+1).padStart(2,'0'));

// Parse "MM-DD" setting into {month, day}
const parseTaxDate = (val, defaultMonth='04', defaultDay='06') => {
  if (!val) return {month:defaultMonth, day:defaultDay};
  const [m,d] = val.split('-');
  return {month:m||defaultMonth, day:d||defaultDay};
};

export default function Settings() {
  const navigate = useNavigate();
  const { settings, setSettings } = useAppData();
  const { user, isAdmin, isOwner } = useAuth();
  // BUG-012: an unverified user can always resend the verification email from here
  const [verifySending, setVerifySending] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState('');
  const handleResendVerification = async () => {
    setVerifySending(true); setVerifyMsg('');
    try { await authAPI.resendVerification(); setVerifyMsg('✅ Verification email sent — check your inbox (and spam folder).'); }
    catch (err) { setVerifyMsg(err?.error || 'Failed to send. Please try again shortly.'); }
    finally { setVerifySending(false); }
  };
  const isOrgOwner = user?.role === 'owner' || !!user?.isSuperAdmin;

  // ORG-DEL-001: Danger Zone state. Loaded once on mount rather than piggy-
  // backed onto the wider Settings save flow — deletion status is its own
  // concern and shouldn't get tangled with the org-name/tax-year form above.
  const [deletionStatus, setDeletionStatus] = useState(null); // { requested, requestedAt, purgeAt }
  const [deletionLoading, setDeletionLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmNameInput, setConfirmNameInput] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteActionLoading, setDeleteActionLoading] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');

  const loadDeletionStatus = () => {
    if (!isOrgOwner) { setDeletionLoading(false); return; }
    orgDeletionAPI.getStatus()
      .then(d => setDeletionStatus(d))
      .catch(() => setDeletionStatus(null))
      .finally(() => setDeletionLoading(false));
  };
  useEffect(() => { loadDeletionStatus(); }, []);

  const [form, setForm] = useState({
    company_name: '',
    default_currency: 'GBP',
    default_jurisdiction: 'england',
    tax_year_start: '04-06',         // personal tax year
  });
  const [loading, setLoading] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [supportCopied, setSupportCopied] = useState(false);
  const [logoSaved, setLogoSaved] = useState(false);
  const orgLogo = settings?.org_logo || null;
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  // MFA state
  const [mfaStatus, setMfaStatus] = useState(null); // null | { mfa_enabled, backup_codes_remaining }
  const [mfaSetup, setMfaSetup] = useState(null);   // { qrCode, secret }
  const [mfaCode, setMfaCode] = useState('');
  const [mfaBackupCodes, setMfaBackupCodes] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [mfaSuccess, setMfaSuccess] = useState('');
  const [showDisableMfa, setShowDisableMfa] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showNewBackupCodes, setShowNewBackupCodes] = useState(false);
  const [backupPassword, setBackupPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSaved, setPwSaved] = useState(false);
  const [emailForm, setEmailForm] = useState(user?.email || '');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailPassword, setEmailPassword] = useState('');

  // Parse tax year date for the personal tax year picker
  const personalTax = parseTaxDate(form.tax_year_start, '04', '06');

  // Sync email from user profile
  useEffect(() => {
    if (user?.email !== undefined) setEmailForm(user.email || '');
  }, [user?.email]);

  useEffect(() => {
    if (settings) {
      setForm(p => ({
        ...p,
        company_name: settings.company_name || '',
        default_currency: settings.default_currency || 'GBP',
        default_jurisdiction: settings.default_jurisdiction || 'england',
        tax_year_start: settings.tax_year_start || '04-06',
      }));
    }
  }, [settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await settingsAPI.update(form);
      setSettings(prev => ({ ...(prev || {}), ...form }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.warn('Settings save error:', err);
    } finally { setLoading(false); }
  };

  // Load MFA status on mount
  useEffect(() => {
    authAPI.mfaStatus().then(setMfaStatus).catch(() => {});
  }, []);

  const handleMfaSetup = async () => {
    setMfaLoading(true); setMfaError(''); setMfaSuccess('');
    try {
      const data = await authAPI.mfaSetup();
      setMfaSetup(data);
    } catch (e) { setMfaError(e.error || 'Failed to start MFA setup'); }
    finally { setMfaLoading(false); }
  };

  const handleMfaConfirm = async () => {
    if (!mfaCode || mfaCode.length < 6) { setMfaError('Please enter the 6-digit code from your authenticator app'); return; }
    setMfaLoading(true); setMfaError('');
    try {
      const data = await authAPI.mfaConfirm(mfaCode);
      setMfaBackupCodes(data.backupCodes);
      setMfaSetup(null); setMfaCode('');
      setMfaStatus(prev => ({ ...prev, mfa_enabled: true, backup_codes_remaining: 8 }));
      setMfaSuccess('MFA enabled successfully! Save your backup codes in a safe place.');
    } catch (e) { setMfaError(e.error || 'Invalid code. Please try again.'); }
    finally { setMfaLoading(false); }
  };

  const handleMfaDisable = async () => {
    if (!disablePassword) { setMfaError('Please enter your password'); return; }
    setMfaLoading(true); setMfaError('');
    try {
      await authAPI.mfaDisable(disablePassword);
      setMfaStatus(prev => ({ ...prev, mfa_enabled: false, backup_codes_remaining: 0 }));
      setShowDisableMfa(false); setDisablePassword('');
      setMfaSuccess('MFA has been disabled.');
    } catch (e) { setMfaError(e.error || 'Failed to disable MFA'); }
    finally { setMfaLoading(false); }
  };

  const handleNewBackupCodes = async () => {
    if (!backupPassword) { setMfaError('Please enter your password'); return; }
    setMfaLoading(true); setMfaError('');
    try {
      const data = await authAPI.mfaNewBackupCodes(backupPassword);
      setMfaBackupCodes(data.backupCodes);
      setShowNewBackupCodes(false); setBackupPassword('');
      setMfaStatus(prev => ({ ...prev, backup_codes_remaining: 8 }));
      setMfaSuccess('New backup codes generated. Save them in a safe place.');
    } catch (e) { setMfaError(e.error || 'Failed to generate backup codes'); }
    finally { setMfaLoading(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwError('');
    if (pwForm.newPassword !== pwForm.confirmPassword) { setPwError('New passwords do not match'); return; }
    const pwProblem = validatePassword(pwForm.newPassword);
    if (pwProblem) { setPwError(pwProblem); return; }
    setPwLoading(true);
    try {
      await authAPI.changePassword(pwForm.currentPassword, pwForm.newPassword);
      setPwSaved(true);
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setPwSaved(false), 4000);
    } catch (err) {
      setPwError(err.error || 'Failed to change password. Check your current password is correct.');
    } finally { setPwLoading(false); }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setEmailError('');
    if (!emailPassword) { setEmailError('Please enter your current password to confirm this change.'); return; }
    setEmailLoading(true);
    try {
      await authAPI.updateEmail(emailForm, emailPassword);
      setEmailSaved(true);
      setEmailPassword('');
      setTimeout(() => setEmailSaved(false), 4000);
    } catch (err) {
      setEmailError(err.error || 'Failed to update email address');
    } finally { setEmailLoading(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500000) { alert('Logo must be under 500KB'); return; }
    setLogoLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result;
        await settingsAPI.update({ org_logo: base64 });
        setSettings(prev => ({ ...(prev || {}), org_logo: base64 }));
        setLogoSaved(true);
        setTimeout(() => setLogoSaved(false), 3000);
        setLogoLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) { alert('Failed to save logo'); setLogoLoading(false); }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Remove the organisation logo?')) return;
    await settingsAPI.update({ org_logo: '' });
    setSettings(prev => ({ ...(prev || {}), org_logo: '' }));
  };

  // Tax year date picker helper
  const TaxYearPicker = ({ label, value, onChange, helpText }) => {
    const parsed = parseTaxDate(value);
    const monthName = MONTHS.find(m => m.value === parsed.month)?.label || parsed.month;
    return (
      <div className="form-group">
        <label>{label}</label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select
            value={parsed.day}
            onChange={e => onChange(`${parsed.month}-${e.target.value}`)}
            style={{ width: '80px' }}
          >
            {DAYS.map(d => <option key={d} value={d}>{parseInt(d)}</option>)}
          </select>
          <select
            value={parsed.month}
            onChange={e => onChange(`${e.target.value}-${parsed.day}`)}
            style={{ flex: 1 }}
          >
            {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
          {helpText} Currently: {parseInt(parsed.day)} {monthName}
        </small>
      </div>
    );
  };

  const handleRequestDeletion = async () => {
    setDeleteError('');
    setDeleteActionLoading(true);
    try {
      // BUG FIX: this previously read form.company_name / settings.company_name
      // — a SEPARATE, independently-editable Settings field that is blank
      // until someone explicitly fills in "Organisation / Company Name"
      // above. The server checks confirmName against organizations.name
      // (set once at registration), which is what user.organizationName
      // reflects. A freshly registered org with no Settings name typed in
      // yet could never pass this check under the old logic — there was
      // nothing correct to type. Matches the same fallback order already
      // used correctly elsewhere on this page (see the Account Details
      // table below) rather than inventing a new one here.
      const orgNameShown = user?.organizationName || settings?.company_name || form.company_name || '';
      if (confirmNameInput.trim().toLowerCase() !== orgNameShown.trim().toLowerCase()) {
        setDeleteError('The name you typed does not match your organisation name shown above.');
        setDeleteActionLoading(false);
        return;
      }
      await orgDeletionAPI.requestDeletion(confirmNameInput);
      setShowDeleteModal(false);
      setConfirmNameInput('');
      loadDeletionStatus();
      setDeleteMessage('Deletion requested. Your account is now read-only and will be permanently deleted in 30 days unless you cancel this request.');
      setTimeout(() => setDeleteMessage(''), 8000);
    } catch (err) {
      setDeleteError(err.error || 'Failed to request deletion. Please try again.');
    } finally {
      setDeleteActionLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    setDeleteActionLoading(true);
    try {
      await orgDeletionAPI.cancelDeletion();
      loadDeletionStatus();
      setDeleteMessage('Deletion request cancelled. Your account is fully active again.');
      setTimeout(() => setDeleteMessage(''), 8000);
    } catch (err) {
      setDeleteError(err.error || 'Failed to cancel deletion request.');
    } finally {
      setDeleteActionLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '1.75rem' }}>
        <div className="page-header-left">
          <h1 className="page-title">Settings</h1>
        </div>
      </div>

      {saved && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>✅ Settings saved successfully!</div>}
      {deleteMessage && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{deleteMessage}</div>}

      {/* ORG-DEL-001: shown to EVERY user in the org, not just the owner —
          a member should know the account they're working in is scheduled
          for deletion, even though only the owner can act on it. Read-only
          enforcement is already active server-side by this point (see
          authenticateToken); this banner is what explains why saves are
          suddenly failing with a 423, rather than leaving that a mystery. */}
      {deletionStatus?.requested && (
        <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:'0.5rem', padding:'1rem 1.25rem', marginBottom:'1.5rem', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.75rem' }}>
          <div>
            <div style={{ fontWeight:700, color:'#991b1b', fontSize:'0.95rem' }}>⚠️ Account scheduled for deletion</div>
            <div style={{ fontSize:'0.82rem', color:'#7f1d1d', marginTop:'0.2rem' }}>
              Your account is read-only and will be permanently deleted on{' '}
              {deletionStatus.purgeAt ? new Date(deletionStatus.purgeAt).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }) : 'the scheduled date'}.
              {' '}This cannot be undone once it happens.
            </div>
          </div>
          {isOrgOwner && (
            <button className="btn btn-primary" disabled={deleteActionLoading} onClick={handleCancelDeletion}>
              {deleteActionLoading ? 'Cancelling…' : 'Cancel Deletion'}
            </button>
          )}
        </div>
      )}

      {/* BUG-012: email verification. Shown while the email is unverified; the 423 message points here and to the banner. */}
      {user && !user.emailVerified && (
        <div style={{ background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:'0.5rem', padding:'1rem 1.25rem', marginBottom:'1.5rem', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.75rem' }}>
          <div>
            <div style={{ fontWeight:700, color:'#92400e', fontSize:'0.95rem' }}>📧 Please verify your email address</div>
            <div style={{ fontSize:'0.82rem', color:'#78350f', marginTop:'0.2rem' }}>
              If the verification window has passed your account is read-only: you can view everything, but adding, changing or deleting is paused until you verify. If the address on your account is wrong, correct it under "Email Address" below first.
            </div>
            {verifyMsg && <div style={{ fontSize:'0.82rem', fontWeight:600, marginTop:'0.4rem' }}>{verifyMsg}</div>}
          </div>
          <button className="btn btn-primary" disabled={verifySending} onClick={handleResendVerification}>
            {verifySending ? 'Sending…' : 'Resend verification email'}
          </button>
        </div>
      )}

      <BillingSettings />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Organisation Settings — owner only */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">🏢 Organisation Settings</span>
            {!isOrgOwner && <span className="badge badge-secondary" style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>Owner only</span>}
          </div>
          {isOrgOwner ? (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Organisation / Company Name</label>
                <input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} placeholder="Your organisation name" />
                <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>Appears in reports and exports</small>
              </div>
              <div className="form-group">
                <label>Default Currency</label>
                <select value={form.default_currency} onChange={e => setForm(p => ({ ...p, default_currency: e.target.value }))}>
                  <option value="GBP">GBP — British Pound (£)</option>
                  <option value="EUR">EUR — Euro (€)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Default Jurisdiction</label>
                <select value={form.default_jurisdiction} onChange={e => setForm(p => ({ ...p, default_jurisdiction: e.target.value }))}>
                  <option value="england">England</option>
                  <option value="scotland">Scotland</option>
                  <option value="wales">Wales</option>
                  <option value="northern_ireland">Northern Ireland</option>
                  <option value="ireland">Ireland 🇮🇪</option>
                </select>
                <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                  Sets default tenancy types, compliance checklists and deposit schemes for new properties.
                  Can be overridden per property.
                </small>
              </div>

              <TaxYearPicker
                label="Personal Tax Year Start"
                value={form.tax_year_start}
                onChange={val => setForm(p => ({ ...p, tax_year_start: val }))}
                helpText="Used for personal property income & expenses."
              />

              <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:'0.5rem', padding:'0.75rem 1rem', marginBottom:'1rem', fontSize:'0.85rem', color:'#1e40af' }}>
                ℹ️ Company tax year and VAT registration are now set per-company on the <a href="/companies" style={{ color:'#1e40af', fontWeight:600 }}>Companies page</a> — each company you hold properties through can have its own accounting year end.
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%' }}>
                {loading ? '⟳ Saving...' : '💾 Save Settings'}
              </button>
            </form>
          ) : (
            <div style={{ padding: '1rem', color: 'var(--text-secondary)', fontSize: '0.875rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔒</div>
              <p>Only the organisation owner can change these settings.</p>
              <p style={{ marginTop: '0.5rem', fontWeight: 600 }}>
                Personal Tax Year: {(() => { const p = parseTaxDate(settings?.tax_year_start || '04-06'); return `${parseInt(p.day)} ${MONTHS.find(m=>m.value===p.month)?.label}`; })()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Email Address Card — available to all users */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header"><span className="card-title">✉️ Email Address</span></div>
        {emailSaved && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>✅ Email address updated!</div>}
        {emailError && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{emailError}</div>}
        <form onSubmit={handleEmailChange}>
          <div className="form-group" style={{ maxWidth: '400px' }}>
            <label>Email Address</label>
            <input
              type="email"
              value={emailForm}
              onChange={e => setEmailForm(e.target.value)}
              placeholder="your@email.com"
              autoComplete="email"
            />
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Used for system notifications and alerts. Leave blank to opt out.
            </small>
          </div>
          <div className="form-group" style={{ maxWidth: '400px' }}>
            <label>Current Password</label>
            <input
              type="password"
              value={emailPassword}
              onChange={e => setEmailPassword(e.target.value)}
              placeholder="Confirm with your current password"
              autoComplete="current-password"
            />
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Required to confirm this change. Changing your email will require re-verification and will notify your old address.
            </small>
          </div>
          <button type="submit" className="btn btn-primary" disabled={emailLoading}>
            {emailLoading ? '⟳ Saving...' : '✉️ Update Email'}
          </button>
        </form>
      </div>

      {/* Logo Upload Card — owner only */}
      {isOrgOwner && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header"><span className="card-title">🖼️ Organisation Logo</span></div>
          {logoSaved && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>✅ Logo saved!</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2rem', alignItems: 'center' }}>
            <div style={{ width: '120px', height: '120px', border: '2px dashed var(--border-light)', borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-light)', overflow: 'hidden', flexShrink: 0 }}>
              {orgLogo ? <img src={orgLogo} alt="Org logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: '3rem' }}>🏠</span>}
            </div>
            <div>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                Upload your organisation logo. Recommended: square image (PNG/JPG), max 500KB.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <label className="btn btn-primary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  {logoLoading ? '⟳ Uploading...' : '📁 Upload Logo'}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} disabled={logoLoading} />
                </label>
                {orgLogo && <button className="btn btn-secondary" onClick={handleRemoveLogo} style={{ color: 'var(--danger)' }}>🗑️ Remove Logo</button>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MFA-003: Two-Factor Authentication */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <span className="card-title">🔐 Two-Factor Authentication (MFA)</span>
          {mfaStatus?.mfa_enabled && <span style={{ background: '#dcfce7', color: '#166534', fontSize: '0.75rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: '0.25rem' }}>✅ Enabled</span>}
        </div>

        {mfaError && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{mfaError}</div>}
        {mfaSuccess && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{mfaSuccess}</div>}

        {/* Backup codes display */}
        {mfaBackupCodes && (
          <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: '#92400e' }}>⚠️ Save these backup codes now — they won't be shown again</div>
            <div style={{ fontFamily: 'monospace', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.35rem', marginBottom: '0.75rem' }}>
              {mfaBackupCodes.map((code, i) => (
                <div key={i} style={{ background: '#fff', padding: '0.35rem 0.6rem', borderRadius: '0.25rem', border: '1px solid #fcd34d', fontSize: '0.875rem', fontWeight: 600, letterSpacing: '0.05em' }}>{code}</div>
              ))}
            </div>
            <p style={{ fontSize: '0.8rem', color: '#92400e', margin: 0 }}>Store these in a password manager or safe place. Each code can only be used once if you lose access to your authenticator app.</p>
            <button className="btn btn-secondary" style={{ marginTop: '0.75rem', fontSize: '0.875rem' }} onClick={() => setMfaBackupCodes(null)}>I've saved these codes ✓</button>
          </div>
        )}

        {!mfaStatus?.mfa_enabled ? (
          /* Not enabled — show setup flow */
          <div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '1rem' }}>
              Add an extra layer of security to your account. Once enabled, you'll need your authenticator app every time you log in.
            </p>
            {!mfaSetup ? (
              <button className="btn btn-primary" onClick={handleMfaSetup} disabled={mfaLoading}>
                {mfaLoading ? 'Setting up...' : '🔐 Enable Two-Factor Authentication'}
              </button>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Step 1 — Scan this QR code</div>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Open your authenticator app (Google Authenticator, Authy, or similar) and scan this code.</p>
                    <img src={mfaSetup.qrCode} alt="MFA QR Code" style={{ width: '180px', height: '180px', border: '1px solid var(--border)', borderRadius: '0.5rem', display: 'block' }} />
                    <details style={{ marginTop: '0.5rem' }}>
                      <summary style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>Can't scan? Enter manually</summary>
                      <code style={{ fontSize: '0.75rem', wordBreak: 'break-all', display: 'block', marginTop: '0.25rem', background: 'var(--bg-light)', padding: '0.5rem', borderRadius: '0.25rem' }}>{mfaSetup.secret}</code>
                    </details>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Step 2 — Enter verification code</div>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Enter the 6-digit code shown in your authenticator app to confirm setup.</p>
                    <div className="form-group">
                      <input
                        value={mfaCode}
                        onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                        placeholder="000000"
                        maxLength={6}
                        style={{ letterSpacing: '0.3em', fontSize: '1.25rem', textAlign: 'center', fontFamily: 'monospace' }}
                        onKeyDown={e => e.key === 'Enter' && handleMfaConfirm()}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="btn btn-secondary" onClick={() => { setMfaSetup(null); setMfaCode(''); setMfaError(''); }}>Cancel</button>
                      <button className="btn btn-primary" onClick={handleMfaConfirm} disabled={mfaLoading || mfaCode.length < 6}>
                        {mfaLoading ? 'Verifying...' : '✅ Confirm & Enable'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Enabled — show status and management options */
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
              <div style={{ background: 'var(--bg-light)', padding: '0.75rem', borderRadius: '0.375rem' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.2rem' }}>Status</div>
                <div style={{ fontWeight: 600, color: '#166534' }}>✅ Active</div>
              </div>
              <div style={{ background: 'var(--bg-light)', padding: '0.75rem', borderRadius: '0.375rem' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.2rem' }}>Backup Codes Remaining</div>
                <div style={{ fontWeight: 600, color: mfaStatus?.backup_codes_remaining <= 2 ? '#dc2626' : 'var(--text-primary)' }}>
                  {mfaStatus?.backup_codes_remaining ?? '—'} of 8
                  {mfaStatus?.backup_codes_remaining <= 2 && <span style={{ fontSize: '0.75rem', color: '#dc2626', marginLeft: '0.5rem' }}>⚠️ Running low</span>}
                </div>
              </div>
            </div>

            {/* Generate new backup codes */}
            {!showNewBackupCodes ? (
              <button className="btn btn-secondary" style={{ marginRight: '0.5rem', fontSize: '0.875rem' }} onClick={() => { setShowNewBackupCodes(true); setMfaError(''); }}>
                🔑 Generate New Backup Codes
              </button>
            ) : (
              <div style={{ background: 'var(--bg-light)', padding: '1rem', borderRadius: '0.5rem', marginBottom: '0.75rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Enter your password to generate new codes</div>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>This will invalidate all existing backup codes.</p>
                <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                  <input type="password" value={backupPassword} onChange={e => setBackupPassword(e.target.value)} placeholder="Your current password" />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-secondary" onClick={() => { setShowNewBackupCodes(false); setBackupPassword(''); }}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleNewBackupCodes} disabled={mfaLoading}>{mfaLoading ? 'Generating...' : 'Generate Codes'}</button>
                </div>
              </div>
            )}

            {/* Disable MFA */}
            {!showDisableMfa ? (
              <button className="btn btn-secondary" style={{ fontSize: '0.875rem', color: 'var(--danger)' }} onClick={() => { setShowDisableMfa(true); setMfaError(''); }}>
                🔓 Disable Two-Factor Authentication
              </button>
            ) : (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', padding: '1rem', borderRadius: '0.5rem', marginTop: '0.75rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#dc2626', fontSize: '0.875rem' }}>⚠️ Disable MFA — this reduces your account security</div>
                <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                  <input type="password" value={disablePassword} onChange={e => setDisablePassword(e.target.value)} placeholder="Enter your password to confirm" />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-secondary" onClick={() => { setShowDisableMfa(false); setDisablePassword(''); }}>Cancel</button>
                  <button className="btn btn-secondary" style={{ color: 'var(--danger)' }} onClick={handleMfaDisable} disabled={mfaLoading}>{mfaLoading ? 'Disabling...' : 'Yes, Disable MFA'}</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Change Password */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header"><span className="card-title">🔑 Change Password</span></div>
        {pwSaved && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>✅ Password changed successfully!</div>}
        {pwError && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{pwError}</div>}
        <form onSubmit={handlePasswordChange}>
          <div className="form-grid">
            <div className="form-group">
              <label>Current Password *</label>
              <input type="password" value={pwForm.currentPassword} onChange={e => setPwForm(p => ({ ...p, currentPassword: e.target.value }))} required placeholder="Your current password" autoComplete="current-password" />
            </div>
            <div className="form-group" />
            <div className="form-group">
              <label>New Password *</label>
              <input type="password" value={pwForm.newPassword} onChange={e => setPwForm(p => ({ ...p, newPassword: e.target.value }))} required placeholder="Choose a strong password" autoComplete="new-password" />
              <PasswordRules password={pwForm.newPassword} />
            </div>
            <div className="form-group">
              <label>Confirm New Password *</label>
              <input type="password" value={pwForm.confirmPassword} onChange={e => setPwForm(p => ({ ...p, confirmPassword: e.target.value }))} required placeholder="Repeat new password" autoComplete="new-password" />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={pwLoading || validatePassword(pwForm.newPassword) !== null} style={{ marginTop: '0.5rem' }}>
            {pwLoading ? '⟳ Changing...' : '🔑 Change Password'}
          </button>
        </form>
      </div>

      {/* Account Info */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        <div className="card">
          <div className="card-header"><span className="card-title">👤 Account Information</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {[['Username', user?.username], ['Email', user?.email || '—'], ['Role', user?.role || 'member'], ['Organisation', settings?.company_name || user?.organizationName || 'Default Organization']].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', borderBottom: '1px solid var(--border-light)' }}>
                <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </div>

        <div>
          {isOrgOwner && (
            <div className="card" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
              <div className="card-header"><span className="card-title" style={{ color: '#1e40af' }}>🔑 Admin Panel</span></div>
              <p style={{ fontSize: '0.875rem', color: '#1e40af', marginBottom: '1rem' }}>
                Manage users, approve registrations and monitor activity.
              </p>
              <a href="/admin" style={{ display: 'block', background: '#1e40af', color: 'white', padding: '0.625rem 1rem', borderRadius: '0.5rem', textAlign: 'center', textDecoration: 'none', fontWeight: 600 }}>
                Open Admin Panel →
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Companies — owner only */}
      {isOrgOwner && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--brand-green)' }}>🏢 Companies</h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            If you hold properties through more than one limited company, manage them here — each can be tracked and reported on separately.
          </p>
          <button className="btn btn-secondary" onClick={() => navigate('/companies')}>Manage Companies →</button>
        </div>
      )}

      {/* Data Export — owner only */}
      {isOrgOwner && (
        <div id="data-export-section" style={{ marginTop: '1.5rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--brand-green)' }}>📦 Data Export</h3>
          <DataExport />
        </div>
      )}

      {/* Danger Zone — ORG-DEL-001. Owner only, matching the requireOwner
          bar on the backend routes, and deliberately visually separated
          (red border, its own heading) from every other card on this page —
          this is the one action on the whole screen that is genuinely
          irreversible after 30 days. */}
      {isOrgOwner && !deletionLoading && (
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#b91c1c' }}>⚠️ Danger Zone</h3>
          <div className="card" style={{ border: '1px solid #fecaca' }}>
            {!deletionStatus?.requested ? (
              <>
                <div style={{ fontWeight: 700, marginBottom: '0.4rem' }}>Delete Account</div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.6 }}>
                  Permanently delete your casavello account and all associated data — properties, tenants, payments,
                  documents, everything. There is a 30-day grace period after you request this: your account becomes
                  read-only immediately, and you can cancel at any time before the 30 days are up. After that, deletion
                  is permanent and cannot be reversed.
                </p>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                  Before deleting, consider <a href="#" onClick={(e) => { e.preventDefault(); document.getElementById('data-export-section')?.scrollIntoView({ behavior: 'smooth' }); }} style={{ color: 'var(--brand-green)' }}>exporting your data</a> above.
                </p>
                <button className="btn btn-danger" onClick={() => { setShowDeleteModal(true); setConfirmNameInput(''); setDeleteError(''); }}>
                  🗑️ Delete My Account
                </button>
              </>
            ) : (
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Deletion already requested — see the notice above to cancel it.
              </div>
            )}
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <span className="modal-title">🗑️ Delete Account</span>
              <button type="button" className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:'0.375rem', padding:'0.75rem 1rem', marginBottom:'1rem', fontSize:'0.82rem', color:'#78350f', lineHeight:1.5 }}>
              This starts a 30-day countdown. Your account becomes read-only immediately. You can cancel any time
              before the 30 days are up from the banner at the top of this page. After 30 days, everything is
              permanently deleted and cannot be recovered.
            </div>
            {deleteError && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{deleteError}</div>}
            <div className="form-group">
              <label>Type your organisation name to confirm: <strong>{user?.organizationName || settings?.company_name || form.company_name || '(not set)'}</strong></label>
              <input
                type="text"
                value={confirmNameInput}
                onChange={e => setConfirmNameInput(e.target.value)}
                placeholder="Type the exact name shown above"
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={deleteActionLoading || !confirmNameInput.trim()}
                onClick={handleRequestDeletion}
              >
                {deleteActionLoading ? 'Submitting…' : 'Permanently Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact & Support — visible to every user regardless of role or
          plan, same reasoning as the sidebar link: a monitored support
          inbox that only existed inside legal document text was effectively
          undiscoverable. This gives it a proper home with actual context,
          alongside the quick sidebar link for when someone just wants to
          click and go. */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div style={{ fontWeight: 700, marginBottom: '0.4rem' }}>💬 Help & Support</div>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
          Something not working, or have a question about your account? We're a small team and read every message.
        </p>
        {/* Same mailto: reliability fix as the sidebar link — copies the
            address to the clipboard on click too, since mailto: alone
            silently does nothing on a browser/OS with no default mail
            client configured. */}
        <a
          href="mailto:info@casavello.io"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText('info@casavello.io');
              setSupportCopied(true);
              setTimeout(() => setSupportCopied(false), 3000);
            } catch (e) { /* mailto: attempt is still the fallback */ }
          }}
          className="btn btn-secondary"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', textDecoration: 'none' }}
        >
          {supportCopied ? '✅ Copied — paste into your email app' : '✉️ info@casavello.io'}
        </a>
      </div>

      {/* Legal */}
      <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border)', textAlign: 'center' }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>casavello by Hibernian Technologies Limited</div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', fontSize: '0.8rem' }}>
          <a href="/privacy" style={{ color: 'var(--brand-green)', textDecoration: 'none' }}>Privacy Policy</a>
          <a href="/terms" style={{ color: 'var(--brand-green)', textDecoration: 'none' }}>Terms of Service</a>
          <a href="/eula" style={{ color: 'var(--brand-green)', textDecoration: 'none' }}>EULA</a>
        </div>
      </div>
    </div>
  );
}
